import { Tooltip } from "antd";
import styled from "styled-components";
import {
  gray300,
  gray700,
  gray900,
  green700,
  red100,
  white,
  yellow400,
} from "core/resources/theme/colors";
import { getRdoOcupacaoIconVisual } from "./getRdoOcupacaoIcon";
import { RDO_STATUS_TIPS } from "./RdoStatusCell";
import { RdoStatusEnum } from "./rdoOperacoes.types";
import { RdoPeriodoStatusSummary } from "./summarizeRdoPeriodoStatus";

type Props = {
  titulo: string;
  ocupacao: string;
  summary: RdoPeriodoStatusSummary;
};

function RdoPeriodoSummaryCard({ titulo, ocupacao, summary }: Props) {
  const iconVisual = getRdoOcupacaoIconVisual(ocupacao);

  return (
    <Card>
      <Top style={{padding: '12px'}}>
        <IconBadge
          aria-hidden
          $background={iconVisual.background}
          $border={iconVisual.border}
        >
          {iconVisual.icon}
        </IconBadge>
        <Meta>
          <Title>{titulo}</Title>
          <OpsLine>
            <ValueCount>{summary.inProgress}</ValueCount> Operações em andamento
          </OpsLine>
        </Meta>
      </Top>
      <Semaforo aria-label="Semáforo de status">
        <SemaforoCol>
          <Tooltip title={RDO_STATUS_TIPS[RdoStatusEnum.EmDia]}>
            <SemaforoHead>
              <Pip $color={green700} /> No prazo
            </SemaforoHead>
          </Tooltip>
          <Tooltip
            title={`${RDO_STATUS_TIPS[RdoStatusEnum.EmDia]} Quantidade: ${summary.emDia}.`}
          >
            <Value>{summary.emDia}</Value>
          </Tooltip>
        </SemaforoCol>
        <SemaforoCol>
          <Tooltip title={RDO_STATUS_TIPS[RdoStatusEnum.EmAtencao]}>
            <SemaforoHead>
              <Pip $color={yellow400} /> Em atenção
            </SemaforoHead>
          </Tooltip>
          <Tooltip
            title={`${RDO_STATUS_TIPS[RdoStatusEnum.EmAtencao]} Quantidade: ${summary.emAtencao}.`}
          >
            <Value>{summary.emAtencao}</Value>
          </Tooltip>
        </SemaforoCol>
        <SemaforoCol>
          <Tooltip title={RDO_STATUS_TIPS[RdoStatusEnum.Atrasada]}>
            <SemaforoHead>
              <Pip $color={red100} /> Atrasadas
            </SemaforoHead>
          </Tooltip>
          <Tooltip
            title={`${RDO_STATUS_TIPS[RdoStatusEnum.Atrasada]} Quantidade: ${summary.atrasadas}.`}
          >
            <Value>{summary.atrasadas}</Value>
          </Tooltip>
        </SemaforoCol>
      </Semaforo>
    </Card>
  );
}

export default RdoPeriodoSummaryCard;

const Card = styled.article`
  border: 1px solid ${gray300};
  border-radius: 8px;
  width: 300px;
  overflow: hidden;
  background: ${white};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const IconBadge = styled.div<{ $background: string; $border: string }>`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${({ $background }) => $background};
  border: 1px solid ${({ $border }) => $border};
  display: grid;
  place-items: center;
  flex-shrink: 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
`;

const Meta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 0.85rem;
  font-weight: 800;
  color: ${gray900};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OpsLine = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: ${gray700};

  strong {
    font-weight: 800;
    color: ${gray900};
  }
`;

const Semaforo = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  border-top: 1px solid ${gray300};
  background: #fafbfc
`;

const SemaforoCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 0;
  padding: 8px 8px;
  text-align: center;

  & + & {
    border-left: 1px solid ${gray300};
  }
`;

const SemaforoHead = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${gray700};
  cursor: help;
`;

const Pip = styled.i<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const Value = styled.strong`
  font-size: 1.15rem;
  font-weight: 800;
  color: ${gray900};
  font-variant-numeric: tabular-nums;
  cursor: help;
  line-height: 1.1;
  text-align: center;
`;

const ValueCount = styled.strong`
  font-size: 1.15rem;
  font-weight: 800;
  color: ${gray900};
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  text-align: center;
`;